import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { maxLengthValidator } from '@common/helpers/validations/max-length.validator';
import { AswValidators } from '@lib/helpers';
import { ValidateOptions, ValidationErrorInfo } from '@lib/interfaces';

const param = (key: number, value: string | number, dontTranslate = false) => {
  return { key: key, value: value, dontTranslate: dontTranslate };
};

const maxLength = (max: number, errorMsgId: string, paramValue: string) => {
  return maxLengthValidator({
    max: max,
    errorMsgId: errorMsgId,
    params: [param(0, paramValue), param(1, max, true)],
  });
};

export function createServiceRequestPaymentInputPresModuleForm(systemDate?: Date): FormGroup<{
  [key: string]: FormControl<any>;
}> {
  // 14.1.1	単位チェックを行う。
  // 下記表のNo.順にチェックを行う。表の最後までチェックを実施し、エラーが存在した場合はエラーメッセージを表示し処理を中断する。
  const form = new FormGroup<{ [key: string]: FormControl }>({
    // (14.1.1.1.4) カード名義
    holderName: new FormControl<string | null>('', {
      validators: [
        AswValidators.required({
          errorMsgId: 'E0001',
          params: {
            key: 0,
            value: 'label.cardholderName',
          },
        }),
        AswValidators.alphaSpace({
          errorMsgId: 'E0004',
          params: {
            key: 0,
            value: 'label.cardholderName',
          },
        }),
        maxLength(40, 'E0013', 'label.cardholderName'),
      ],
    }),
    // (14.1.1.1.5) カード有効期限
    expiryDate: new FormControl<string | null>(null, {
      validators: [
        validateSelectYm(
          {
            errorMsgId: 'E0001',
            params: {
              key: 0,
              value: 'label.expirationDate',
            },
          },
          systemDate
        ),
        AswValidators.numeric({
          errorMsgId: 'E0003',
          params: {
            key: 0,
            value: 'label.expirationDate',
          },
        }),
      ],
    }),
    // (14.1.1.1.6) CVV(※2)
    cvv: new FormControl<string | null>('', {
      validators: [
        AswValidators.required({
          errorMsgId: 'E0001',
          params: {
            key: 0,
            value: 'label.securityCode',
          },
        }),
        AswValidators.numeric({
          errorMsgId: 'E0003',
          params: {
            key: 0,
            value: 'label.securityCode',
          },
        }),
        AswValidators.lengths({
          min: 3,
          errorMsgId: 'E0014',
          params: [
            {
              key: 0,
              value: 'label.securityCode',
            },
            {
              key: 1,
              value: 3,
            },
            {
              key: 2,
              value: 4,
            },
          ],
        }),
        maxLengthValidator({
          max: 4,
          errorMsgId: 'E0014',
          params: [
            {
              key: 0,
              value: 'label.securityCode',
            },
            {
              key: 1,
              value: 3,
            },
            {
              key: 2,
              value: 4,
            },
          ],
        }),
      ],
    }),
    // (14.1.1.1.7) 名義人メールアドレス
    cardHolderEmail: new FormControl<string | null>(null, {
      validators: [
        AswValidators.required({
          errorMsgId: 'E0001',
          params: {
            key: 0,
            value: 'label.eMailAddress',
          },
        }),
        AswValidators.email({
          patternMsgId: 'E0017',
          params: {
            key: 0,
            value: 'label.eMailAddress',
          },
        }),
        maxLength(50, 'E0013', 'label.eMailAddress'),
      ],
    }),
    // (14.1.1.1.8) 名義人メールアドレス確認
    cardHolderConfirmEmail: new FormControl<string | null>(null, {
      validators: [
        AswValidators.required({
          errorMsgId: 'E0001',
          params: {
            key: 0,
            value: 'label.confirmMailAddress',
          },
        }),
        AswValidators.email({
          patternMsgId: 'E0017',
          params: {
            key: 0,
            value: 'label.confirmMailAddress',
          },
        }),
        maxLength(50, 'E0013', 'label.confirmMailAddress'),
      ],
    }),
    // (14.1.1.1.9) 電話番号の国番号選択
    cardHolderPhoneCountry: new FormControl<string | null>(null, {
      validators: [
        AswValidators.required({
          errorMsgId: 'E0001',
          params: {
            key: 0,
            value: 'label.countryCode',
          },
        }),
      ],
    }),
    // (14.1.1.1.10) 電話番号
    cardHolderPhoneNumber: new FormControl<string | null>(null, {
      validators: [
        AswValidators.required({
          errorMsgId: 'E0001',
          params: {
            key: 0,
            value: 'label.cardHolderTelNumber',
          },
        }),
        maxLength(16, 'E0013', 'label.cardHolderTelNumber'),
      ],
    }),
    saveAsBasicReservationInformation: new FormControl(false),
    saveAsUsualCheckboxControl: new FormControl(false),
    uATPCardChecked: new FormControl(false),
  });
  return form;
}

export function addNotificationEmailFormControlToFormGroup(
  paymentInputFG: FormGroup,
  travelerId: string,
  emailAddressValue: string,
  emailAddressConfirmValue: string
): void {
  // (14.1.1.1.15) 搭乗者メールアドレス(※6)
  const emailFC = new FormControl(emailAddressValue, [
    AswValidators.required({
      errorMsgId: 'E0001',
      params: {
        key: 0,
        value: 'label.eMailAddress',
      },
    }),
    AswValidators.email({
      patternMsgId: 'E0017',
      params: {
        key: 0,
        value: 'label.eMailAddress',
      },
    }),
    maxLength(50, 'E0013', 'label.eMailAddress'),
  ]);
  // (14.1.1.1.16) 搭乗者メールアドレス(確認用)(※6)
  const emailConfirmFC = new FormControl(emailAddressConfirmValue, [
    AswValidators.required({
      errorMsgId: 'E0001',
      params: {
        key: 0,
        value: 'label.confirmMailAddress',
      },
    }),
    AswValidators.email({
      patternMsgId: 'E0017',
      params: {
        key: 0,
        value: 'label.confirmMailAddress',
      },
    }),
    maxLength(50, 'E0013', 'label.confirmMailAddress'),
  ]);

  paymentInputFG.addControl('emailAddress_' + travelerId, emailFC);
  paymentInputFG.addControl('emailAddressConfirm_' + travelerId, emailConfirmFC);
}

export function addNotificationSMSlFormControlToFormGroup(
  paymentInputFG: FormGroup,
  travelerId: string,
  phoneCountryExtension: string,
  countryCode: string,
  number: string
): void {
  // (14.1.1.1.18) 搭乗者SMS国番号(※1)
  const numberCountryFC = new FormControl(countryCode, [
    AswValidators.required({
      errorMsgId: 'E0002',
      params: {
        key: 0,
        value: 'label.countryCode',
      },
    }),
  ]);
  // (14.1.1.1.19) 搭乗者SMS番号(※6)
  const numberFC = new FormControl(number, [
    AswValidators.required({
      errorMsgId: 'E0001',
      params: {
        key: 0,
        value: 'label.telephoneNumber',
      },
    }),
    AswValidators.phoneNumber({
      telCountryCode: phoneCountryExtension,
      params: {
        key: 0,
        value: 'label.telephoneNumber',
      },
    }),
  ]);
  paymentInputFG.addControl('numberCountry_' + travelerId, numberCountryFC);
  paymentInputFG.addControl('number_' + travelerId, numberFC);
}
export function addNotificationEmails(paymentInputFG: FormGroup): void {
  // (14.1.1.1.12) 申込完了連絡先メールアドレス(※1)
  const notificationConfirmationEmail = new FormControl<string | null>(null, {
    validators: [
      AswValidators.required({
        errorMsgId: 'E0001',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      }),
      AswValidators.email({
        patternMsgId: 'E0017',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      }),
    ],
  });
  // (14.1.1.1.13) 申込完了連絡先メールアドレス(確認用)(※1)
  const notificationConfirmationConfirmEmail = new FormControl<string | null>(null, {
    validators: [
      AswValidators.required({
        errorMsgId: 'E0001',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      }),
      AswValidators.email({
        patternMsgId: 'E0017',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      }),
    ],
  });
  paymentInputFG.addControl('notificationConfirmationEmail', notificationConfirmationEmail);
  paymentInputFG.addControl('notificationConfirmationConfirmEmail', notificationConfirmationConfirmEmail);
}

function compareDates(dateA: Date, dateB: string) {
  let parsedDateA = new Date(dateA);
  let year = parseInt(dateB.substring(0, 4), 10);
  let month = parseInt(dateB.substring(4, 6), 10) - 1;
  let parsedDateB = new Date(year, month);
  return parsedDateB < parsedDateA;
}

function validateSelectYm(option?: ValidateOptions, systemDate?: Date): ValidatorFn {
  return (control: AbstractControl): { [key: string]: ValidationErrorInfo } | null => {
    if (control.value && control.value.length === 6) {
      if (compareDates(systemDate ?? new Date(), control.value)) {
        return {
          'validate-format-selectYm': {
            errorMsgId: 'E1783',
            params: option?.params,
          },
        };
      }

      return null;
    } else {
      return {
        'validate-required-selectYm': {
          errorMsgId: option?.errorMsgId ?? 'validationError_selectDateYm',
          params: option?.params,
        },
      };
    }
  };
}

function confirmEmailValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return {
      'validate-email': {
        errorMsgId: 'E0461',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      } as ValidationErrors,
    };
  };
}

export function updateConfirmEmailValidator(
  paymentInputFG: FormGroup,
  emailFCName: string,
  emailConfirmFCName: string
): void {
  const email = paymentInputFG.get(emailFCName)?.value;
  const confirmEmail = paymentInputFG.get(emailConfirmFCName)?.value;
  if (email && confirmEmail && email !== confirmEmail) {
    paymentInputFG.get(emailFCName)?.markAsTouched();
    paymentInputFG.get(emailConfirmFCName)?.setValidators([
      AswValidators.required({
        errorMsgId: 'E0001',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      }),
      AswValidators.email({
        patternMsgId: 'E0017',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      }),
      confirmEmailValidator(),
    ]);
  } else {
    paymentInputFG.get(emailConfirmFCName)?.setValidators([
      AswValidators.required({
        errorMsgId: 'E0001',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      }),
      AswValidators.email({
        patternMsgId: 'E0017',
        params: {
          key: 0,
          value: 'label.confirmMailAddress',
        },
      }),
    ]);
  }
  paymentInputFG.get(emailConfirmFCName)?.updateValueAndValidity();
}

export function addCardNumberInput(): FormControl<string | null> {
  // (14.1.1.1.3) カード番号 (※6)
  const cardNumber = new FormControl<string | null>(null, {
    validators: [
      AswValidators.required({
        errorMsgId: 'E0001',
        params: {
          key: 0,
          value: 'label.creditCardNumber',
        },
      }),
      AswValidators.numeric({
        errorMsgId: 'E0003',
        params: {
          key: 0,
          value: 'label.creditCardNumber',
        },
      }),
      AswValidators.lengths({
        min: 11,
        errorMsgId: 'E0014',
        params: [
          {
            key: 0,
            value: 'label.creditCardNumber',
          },
          {
            key: 1,
            value: 11,
          },
          {
            key: 2,
            value: 16,
          },
        ],
      }),
      maxLengthValidator({
        max: 16,
        errorMsgId: 'E0014',
        params: [
          {
            key: 0,
            value: 'label.creditCardNumber',
          },
          {
            key: 1,
            value: 11,
          },
          {
            key: 2,
            value: 16,
          },
        ],
      }),
    ],
  });

  return cardNumber;
}
// clear validation
export function clearCardNumberValidation(paymentInputFG: FormGroup) {
  paymentInputFG.get('cardNumber')?.clearValidators();
  paymentInputFG.get('cardNumber')?.updateValueAndValidity();
}

// enable validation
export function enableCardNumberValidation(paymentInputFG: FormGroup) {
  paymentInputFG.get('cardNumber')?.setValidators([
    AswValidators.required({
      errorMsgId: 'E0001',
      params: {
        key: 0,
        value: 'label.creditCardNumber',
      },
    }),
    AswValidators.numeric({
      errorMsgId: 'E0003',
      params: {
        key: 0,
        value: 'label.creditCardNumber',
      },
    }),
    AswValidators.lengths({
      min: 11,
      errorMsgId: 'E0014',
      params: [
        {
          key: 0,
          value: 'label.creditCardNumber',
        },
        {
          key: 1,
          value: 11,
        },
        {
          key: 2,
          value: 16,
        },
      ],
    }),
    maxLengthValidator({
      max: 16,
      errorMsgId: 'E0014',
      params: [
        {
          key: 0,
          value: 'label.creditCardNumber',
        },
        {
          key: 1,
          value: 11,
        },
        {
          key: 2,
          value: 16,
        },
      ],
    }),
  ]);
  paymentInputFG.get('cardNumber')?.updateValueAndValidity();
}

export function addIssuerReceiptEmailInput(): FormControl<string | null> {
  // (14.1.1.1.11) 領収書の発行名義 (※1)
  const issuerReceiptEmail = new FormControl<string | null>(null, {
    validators: [
      AswValidators.required({
        errorMsgId: 'E0002',
        params: {
          key: 0,
          value: 'label.receiptName',
        },
      }),
      maxLength(40, 'E0013', 'label.receiptName'),
      AswValidators.shiftJis({
        errorMsgId: 'E0351',
        params: {
          key: 0,
          value: 'label.receiptName',
        },
      }),
    ],
  });
  return issuerReceiptEmail;
}
